<template>
  <div class="page-content">
    <div id="RulesContent">
      <div id="title">
        <h1>Regole della Briscola Chiamata</h1>
      </div>
<h2>Preambolo</h2>
<p>La Briscola Chiamata è un gioco delle carte altamente strategico e dalle complesse dinamiche, per 5, 6 o 8 giocatori. Si gioca con un mazzo da 40 carte e le regole di base per la presa di punti sono quelle della <a href="https://it.wikipedia.org/wiki/Briscola">Briscola</a> (non riviste in dettaglio qui), con la differenza che le due squadre di antagonisti vengono determinate ad ogni mano, tramite un’asta che viene utilizzata per determinare la briscola. Il gioco viene anche chiamato, appunto, “briscola chiamata”.</p>
<h2>Definizioni e glossario di base</h2>
<p>
  <table>
    <tr>
      <td>Comandante</td><td>Il giocatore che vince l’asta iniziale e chiama la briscola</td>
    </tr>
    <tr>
      <td>Socio</td><td>Il giocatore che ha la carta chiamata dal comandante</td>
    </tr>
    <tr>
      <td>Asta</td><td>Fase iniziale in cui si stabilisce il comandante e la carta chiamata</td>
    </tr>
    <tr>
      <td>Chiamata in mano</td><td>Chiamata in cui il comandante sceglie una carta che lui possiede</td>
    </tr>
    <tr>
      <td>“Andare sotto”</td><td>Chiamata in cui si dichiara di fare più di 61 punti</td>
    </tr>
    <tr>
      <td>Chiamata doppia</td><td>Andare sotto da 61 a 79 punti</td>
    </tr>
    <tr>
      <td>Chiamata tripla</td><td>Andare oltre i 80 punti</td>
    </tr>
    <tr>
      <td>Cappotto</td><td>Una delle due squadre fa tutti i punti</td>
    </tr>
  </table>
</p>
<h2>Regole e principi di base del gioco</h2>
<p>Il mazziere distribuisce le carte, otto a testa. Comincia quindi un’asta durante la quale, a turno, in senso antiorario, partendo dal giocatore alla destra del mazziere, ogni giocatore ha la possibilità di “chiamare” o di “passare”: l’asta procede in senso antiorario ed in modo decrescente, cioè si possono chiamare solo carte progressivamente più basse secondo l’ordine</p>
<p>A > 3 > R > D > F > 7 > 6 > 5 > 4 > 2</p>
<p>Chi “passa” è escluso dalla prosecuzione dell’asta.</p>
<p>Ad esempio, avendo 5 giocatori, con il giocatore 1 che fa da mazziere:
  <ul>
    <li>Giocatore 1: distribuisce le carte</li>
    <li>Giocatore 2: chiama l’<b>Asso</b>, ha in mano un 3 e un Re e cerca un compagno che completi le sue carte</li>
    <li>Giocatore 3: <b>passa</b>, non ha buone carte</li>
    <li>Giocatore 4: chiama il <b>3</b>, ha in mano un Asso, un Re e una Donna</li>
    <li>Giocatore 5: <b>passa</b></li>
    <li>Giocatore 1: <b>passa</b></li>
  </ul>
</p>
<p>L’asta non è finita, ci sono ancora due giocatori in gioco che possono rilanciarsi
  <ul>
    <li>Giocatore 2: chiama la <b>Donna</b> (o cavallo), subito sotto il Re che ha in mano</li>
    <li>Giocatore 4: chiama il <b>Fante</b>, ha belle carte</li>
    <li>Giocatore 2: <b>passa</b>, non se la sente di scendere sotto il fante con solo 3 e Re</li>
    <li>Giocatore 4: <b>vince la chiamata</b>, ha chiamato il fante e annuncia quindi il seme che sarà la briscola sfruttando il fatto di avere Asso, Re e Donna e di avere il compagno con il Fante sceglierà appunto il seme in cui ha queste carte</li>
  </ul>
</p>
<br />
<p><small><i>N.B.: nella briscola ci sono in palio 120 punti, per vincere se ne devono fare quindi 61</i></small></p>
<br />
<p>Essendo la carta più bassa “chiamabile” il <b>2</b>, (da cui il fatto che in alcune regioni la briscola chiamata viene anche conosciuta con il nome “Il due”), si potrebbe pensare che l’asta termina lì, ma per poterla fare proseguire ci vengono in aiuto i punti a disposizione per vincere.</p>
<p>L’asta può continuare ulteriormente una volta arrivati al 2, “andando sotto” e cioè dichiarando di fare più dei canonici 61 punti necessari per la vittoria in una mano normale. Quindi in un caso in cui un giocatore arrivi a chiamare il <b>2</b>, un altro giocatore che volesse proseguire l’asta può dire di chiamare il <b>2 a 62 punti</b>, ovvero dichiara che per vincere sarà in grado di fare 62 punti invece dei 61 richiesti, e così via, salendo col punteggio a piacere.</p>
<p>Il vincitore dell’asta diventa <b>comandante</b> e sceglie la carta chiamata, il cui possessore sarà il <b>socio</b> ed il cui seme determina la briscola per quella mano. Chiaramente, i partecipanti all’asta chiamano di preferenza carte che non hanno in mano. La chiamata “andando sotto” è più rischiosa ma mette anche in palio più punti (“doppia” o “tripla”). Per convenzione, quando si chiamano i “punti” la partita vale doppio (a seconda dei posti, la doppia è già con i primi punti o la doppia è solo dopo i 70 punti). Oltre gli 80 punti “chiamati” la partita vale il triplo del normale.
Una volta conclusa l’asta e dichiarata la carta chiamata, il gioco comincia e procede secondo le regole della briscola, sempre partendo dal giocatore alla destra del mazziere, a prescindere da chi ha vinto la chiamata, con la differenza che le due squadre (comandante+socio contro gli altri tre giocatori) non saranno note finché il socio non si sarà rivelato (giocando la carta chiamata, oppure adottando un comportamento di gioco che evidenzi il suo stato).
</p>
<h2>Svolgimento del gioco</h2>
<p>In questa sezione rivediamo in modo esplicito le varie fasi di una partita.</p>
<p>
  <ul>
    <li>Il mazziere distribuisce le carte in senso antiorario. Il giocatore alla sua destra inizia l’asta e sarà anche il primo a giocare, ad asta conclusa.</li>
    <li>A turno, ogni giocatore “chiama” o “passa”. Concluso il primo giro, l’asta continua tra i giocatori che non hanno passato. Le carte sono chiamate in ordine discendente, cioè un giocatore può solo chiamare una carta più bassa di quella chiamata dal giocatore precedente. Il primo giocatore che non passa può cominciare dall’asso. </li>
    <li>L’asta si conclude quando un ultimo giocatore è rimasto a chiamare, cioè nessun altro vuole chiamare una carta più bassa o “andare sotto” di un punteggio più alto.Il comandante dichiare la carta scelta ed il gioco procede secondo le regole della briscola, a partire dal primo giocatore a destra del mazziere. </li>
    <li>Il gioco procede come nella briscola. Il vincitore di ogni mano gioca per primo. Si continua finché non si esauriscono le carte.</li>
    <li>Conteggio dei punti per determinare la squadra vincitrice.
    <ul>
      <li>A = 11</li>
      <li>3 = 10</li>
      <li>Re = 4</li>
      <li>Donna (cavallo) = 3</li>
      <li>Fante = 2</li>
    </ul>
    </li>
  </ul>
</p>
<h2>Gestione del punteggio</h2>
<p>In una partita di briscola chiamata il punteggio attribuito è il seguente:</p>
<p>
  <table>
    <tr>
      <th></th><th>Vittoria</th><th>Sconfitta</th>
    </tr>
    <tr>
      <td>Comandante</td><td>+2</td><td>-2</td>
    </tr>
    <tr>
      <td>Socio</td><td>+1</td><td>-1</td>
    </tr>
    <tr>
      <td>Antagonista 1</td><td>+1</td><td>-1</td>
    </tr>
    <tr>
      <td>Antagonista 2</td><td>+1</td><td>-1</td>
    </tr>
    <tr>
      <td>Antagonista 3</td><td>+1</td><td>-1</td>
    </tr>
  </table>
</p>
<p>In caso di partita doppia o tripla i punteggi in palio vengono raddoppiati o triplicati.</p>
<p>Nel caso particolare in cui il <b>comandante</b> si <b>chiami in mano</b>, quindi scelga di chiamare una carta che possiede, i punteggi cambiano riflettendo il fatto di essere uno contro quattro. Quindi al comandante verranno dati o tolti 4 punti in caso di vittoria o sconfitta, e 1 punto a tutti gli altri giocatori.</p>

<h2>La patta</h2>
<p>Una partita di Briscola Chiamata può anche finire in <b>pareggio</b>: ciò può avvenire nel caso in cui entrambe le squadre realizzino 60 punti. In questo caso alla partita non viene attribuito alcun punteggio e il punto viene aggiunto alla partita successiva.</p>
<p>Il mazziere passa al giocatore seguente l’attuale mazziere e si riparte, ma una partita normale varrà doppia, una doppia sarà tripla e una tripla sarà quadrupla.</p>
<p>Successive patte non fanno altro che aggiungersi al valore della prima partita valida.</p>
<p>Attenzione, la “patta” è una situazione verificabile esclusivamente nel caso in cui non si sono utilizzati i punti in fase di chiamata. Infatti, se un giocatore in chiamata avesse dichiarato di poter vincere con un “Due a 64 punti”, se realizzasse 64 punti avrebbe soddisfatto il contratto stipulato con gli altri giocatori, e sarebbe quindi, insieme al suo socio, il vincitore della partita.</p>
<p>Un caso particolare della “patta” è quella in cui tutti i giocatori passino la chiamata, anche in questo caso si applicano le regole della patta.</p>

<h2>Il cappotto</h2>
<p>Il <b>cappotto</b> si verifica quando una squadra realizza tutti i punti in palio (120), in questo caso il punteggio della partita raddoppia. Quindi una partita normale verrà conteggiata doppia, una doppia sarà quadrupla, una tripla sarà sestupla e così via.</p>

<h2>Buttare giù</h2>
<p>Quando, al termine della chiamata, un giocatore scopre di non avere in mano né punti né briscole può buttare le carte sul tavolo e chiedere che la partita sia nulla. In questo caso valgono i meccanismi già descritti per la “patta”, quindi i punti non giocati vengono portati alla partita successiva, con una particolarità: se la partita invalidata era doppia o tripla (o più) tutti i punti vengono messi sulla partita successiva.</p>

<h2>Le convenzioni di briscolachiamata.online</h2>
<p>Il sito briscolachiamata.online sposa le regole descritte qui sopra, e utilizza alcune convenzioni:</p>
<p><ul><li>Si chiamano prima le carte e poi i punti</li>
    <li>La doppia scatta a 62 punti</li>
    <li>La tripla scatta ad 80 punti</li>
    <li>Si sale di 2 punti o più alla volta durante la chiamata (quindi non si può fare 62 e poi 63, ma si dichiarerà 64)</li>
    <li>Non è implementato il meccanismo del “buttare giù”, ma sarà realizzato in seguito</li>
  </ul></p>

<h2>Le varianti (di prossima implementazione)</h2>
<p>La briscola chiamata è tradizionalmente giocata in 5, ma si posso usare varianti che prevedano anche più giocatori.</p>

<p>Si può giocare in <b>6 giocatori</b> avendo l’accortezza di togliere i 4 dal mazzo, ogni giocatore avrà quindi 6 carte e nella chiamata si passa dal 5 al 2.</p>
<p>Cambiano leggermente i punteggi perché il comandante vittorioso si vedrà attribuire 3 punti invece che 2, e -3 in caso di sconfitta; questo perché in due contro quattro si prende un maggior rischio. Chiamandosi in mano riceverà o perderà 5 punti.</p>

<p>Si può giocare in <b>7 giocatori</b> usando lo stesso meccanismo della variante a 6 giocatori ma con un “morto”: il giocatore che fa il mazzo non prende parte alla partita.</p>

<p>Si può giocare in <b>8 giocatori</b> con il mazzo completo, distribuendo 5 carte a testa.
In questa variante le squadre sarebbero molto sbilanciate (due contro sei), quindi, per convenzione, se un giocatore arriva a chiamare il <b>2</b> anche chi è in possesso del <b>7 dello stesso seme</b> è parte della squadra del comandante. Ne viene che sia molto conveniente andare a chiamare il 2 e che quindi raramente si chiamino carte più alte; in una partita con 6 giocatori nella stessa squadra una singola mano potrebbe concludere la partita (più di 60 punti in palio).
I punti attribuiti ai giocatori variano a seconda della composizione delle squadre:<br/>
Nel caso di due vs sei il comandante riceverà o perderà 4 punti e il suo socio 2 (1 invece per gli altri)<br/>
Nel caso di tre vs cinque il comandante riceverà o perderà 3 punti e i suoi soci 1 punto.<br/>
Nel caso di chiamata in mano il comandante riceverà o perderà 7 punti</p>
    </div>
  </div>
</template>

<script>
export default {
  name: "Rules",
};
</script>
<style scoped>
#RulesContent {
  padding: 4em;
  padding-top: 0em;
  padding-bottom: 5em;
  text-align: left;
}
h2 {
  padding: 1em;
  padding-top: 2em;
}
p {
  padding-bottom: 0.5em;
}
#title {
  display: flex;
  justify-content: center;
}
</style>
